import type { CmmdCloudProjectRemovalOperation } from "~/environments/primary/runtimeConnectionClient";
import { newCommandId } from "../../lib/utils";
import { removalPhaseLabel } from "./cloudRemovalPresentation";
import { REMOVAL_STEP_LABELS, type RemovalStepLabel } from "./removalStepLabels";
import {
  createCmmdControlPlaneClient,
  readDesktopCloudBootstrap,
} from "./sidebarProjectRemovalControlPlaneClient";

const OPERATION_TIMEOUT_MS = 30_000;
const POLL_BUDGET_MS = 120_000;

export interface CloudCheckoutDeletionAuthority {
  readonly previewToken: string;
  readonly forceDirty: boolean;
}

class RemoveProjectOperationTimeoutError extends Error {
  constructor(operationLabel: string) {
    super(`${operationLabel} did not finish within 30 seconds. Please retry this project.`);
    this.name = "RemoveProjectOperationTimeoutError";
  }
}

export async function withRemoveProjectOperationTimeout<T>(
  operationLabel: string,
  operation: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  let timeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = globalThis.setTimeout(() => {
      controller.abort();
      reject(new RemoveProjectOperationTimeoutError(operationLabel));
    }, OPERATION_TIMEOUT_MS);
  });

  try {
    return await Promise.race([operation(controller.signal), timeout]);
  } finally {
    if (timeoutId !== null) globalThis.clearTimeout(timeoutId);
  }
}

async function waitForCloudProjectRemoval(input: {
  readonly operationId: string;
  readonly onProgress: (step: RemovalStepLabel) => void;
}): Promise<CmmdCloudProjectRemovalOperation> {
  const client = createCmmdControlPlaneClient(readDesktopCloudBootstrap());
  const deadline = Date.now() + POLL_BUDGET_MS;
  for (;;) {
    const operation = await withRemoveProjectOperationTimeout(
      REMOVAL_STEP_LABELS.waitForRemoval,
      (signal) => client.getCloudProjectRemoval({ operationId: input.operationId, signal }),
    );
    input.onProgress(removalPhaseLabel(operation.phase));
    if (operation.status === "completed") return operation;
    if (operation.status === "cancelled") {
      throw new Error(
        operation.lastErrorMessage ??
          "CMMD cancelled permanent project removal because its authorization changed.",
      );
    }
    if (operation.status === "retryable") {
      const detail = operation.lastErrorMessage ?? "CMMD could not finish project removal.";
      const code = operation.lastErrorCode ? ` (${operation.lastErrorCode})` : "";
      throw new Error(`${detail}${code} Removal operation: ${operation.id}`);
    }
    if (Date.now() >= deadline) {
      throw new Error(
        `CMMD is still removing this project. The durable operation will continue in the background. Removal operation: ${operation.id}`,
      );
    }
    await new Promise<void>((resolve) => globalThis.setTimeout(resolve, 750));
  }
}

export async function removeCloudRegistrationWithSaga(input: {
  readonly registrationProjectId: number;
  readonly deletePlanningProject: boolean;
  readonly checkoutDeletion?: CloudCheckoutDeletionAuthority;
  readonly onProgress: (step: RemovalStepLabel) => void;
}): Promise<CmmdCloudProjectRemovalOperation> {
  const client = createCmmdControlPlaneClient(readDesktopCloudBootstrap());
  const operation = await withRemoveProjectOperationTimeout(
    REMOVAL_STEP_LABELS.unregister,
    (signal) =>
      client.requestCloudProjectRemoval({
        projectId: input.registrationProjectId,
        idempotencyKey: newCommandId(),
        deletePlanningProject: input.deletePlanningProject,
        ...(input.checkoutDeletion
          ? {
              deleteCheckoutFiles: true,
              previewToken: input.checkoutDeletion.previewToken,
              forceDirty: input.checkoutDeletion.forceDirty,
            }
          : {}),
        signal,
      }),
  );
  return waitForCloudProjectRemoval({
    operationId: operation.id,
    onProgress: input.onProgress,
  });
}
